import React, { useCallback, useEffect, useMemo, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import { Button, Tooltip } from '@mui/material';
import { useUnit } from 'effector-react';
import { MRT_ColumnDef } from 'material-react-table';
import { useSnackbar } from 'notistack';

import { QuestionDialog } from './QuestionDialog';
import { TableGridLocal } from '../../../../components/TableGridLocal';
import { ConfirmationDialog } from '../../../../components/UIKit/ConfirmationDialog';
import { Faq$, faqApi } from '../../../../effector/faq';
import { Question } from '../../../../types/faq';
import { getMessageFromFailedRequest } from '../../../../utils';

export const Questions: React.FC = () => {
  const { questions } = useUnit(Faq$);
  const { enqueueSnackbar } = useSnackbar();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [deleteCandidate, setDeleteCandidate] = useState<Question | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  const loadQuestions = useCallback(() => {
    faqApi.getQuestions('');
  }, []);

  useEffect(() => {
    loadQuestions();
  }, [loadQuestions]);

  const handleAddQuestion = useCallback(() => {
    setEditingQuestion(null);
    setDialogOpen(true);
  }, []);

  const handleEditQuestion = useCallback((question: Question) => {
    setEditingQuestion(question);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setEditingQuestion(null);
    setDeleteCandidate(null);
    setDeleteError('');
  }, []);

  const handleUpdate = useCallback(() => {
    handleCloseDialog();
    loadQuestions();
  }, [handleCloseDialog, loadQuestions]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteCandidate) return;

    setIsDeleting(true);
    setDeleteError('');

    try {
      await faqApi.removeQuestion(deleteCandidate.id);
      enqueueSnackbar('Вопрос удален');
      handleUpdate();
    } catch (e) {
      const message = getMessageFromFailedRequest(e as Error);
      setDeleteError(message);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteCandidate, enqueueSnackbar, handleUpdate]);

  const columns = useMemo<MRT_ColumnDef<Question>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'Идентификатор',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'title',
        header: 'Название',
        filterVariant: 'text',
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        accessorKey: 'text',
        header: 'Текст',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
      },
      {
        id: 'edit',
        header: '',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <Tooltip title="Редактировать">
            <EditNoteRoundedIcon
              sx={{ cursor: 'pointer', marginTop: 0.5 }}
              onClick={() => handleEditQuestion(original)}
            />
          </Tooltip>
        ),
      },
      {
        id: 'remove',
        header: '',
        enableColumnFilter: false,
        enableColumnOrdering: false,
        enableColumnFilterModes: false,
        enableColumnActions: false,
        enableSorting: false,
        size: 40,
        Cell: ({
          cell: {
            row: { original },
          },
        }) => (
          <Tooltip title="Удалить">
            <DeleteOutlineRoundedIcon
              sx={{ cursor: 'pointer', marginTop: 0.5, color: 'error.main' }}
              onClick={() => setDeleteCandidate(original)}
            />
          </Tooltip>
        ),
      },
    ],
    [handleEditQuestion],
  );

  return (
    <>
      <Button variant="contained" color="primary" startIcon={<AddRoundedIcon />} onClick={handleAddQuestion}>
        Добавить
      </Button>

      <div style={{ marginTop: '30px' }}>
        <TableGridLocal
          name="administration-faq-questions"
          columns={columns}
          data={questions}
          totalRowCount={questions.length}
          onFetchData={loadQuestions}
        />
      </div>

      {dialogOpen && <QuestionDialog question={editingQuestion} onClose={handleCloseDialog} onUpdate={handleUpdate} />}

      {deleteCandidate && (
        <ConfirmationDialog
          title="Удаление вопроса"
          description="Вы уверены, что хотите удалить вопрос?"
          error={deleteError}
          isLoading={isDeleting}
          onConfirm={handleDeleteConfirm}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
};
